import type { IngestionStage } from "@sagot/ui";
import { z } from "zod";

/**
 * Backend pipeline stages (`app/rag/ingest/progress.py` `Stage`) folded into the six console
 * stages: Uploading → Parsing (OCR) → Chunking → Contextualizing → Embedding → Ready.
 */
const STAGE_MAP: Readonly<Record<string, IngestionStage>> = {
  upload: "uploading",
  detect: "parsing",
  parse: "parsing",
  images: "parsing",
  normalize_chunk: "chunking",
  enrich: "contextualizing",
  embed: "embedding",
  index: "embedding",
  finalize: "ready",
};

/** Counters of the source's sync run carried by an event (`RunSnapshot`). */
export const runSnapshotSchema = z.object({
  state: z.string(),
  pages_total: z.number().int().min(0),
  pages_done: z.number().int().min(0),
  pages_failed: z.number().int().min(0),
});
export type RunSnapshot = z.infer<typeof runSnapshotSchema>;

/**
 * `GET /v1/sources/events` payload (`SourceEvent`). `status` is about the version (document) the
 * event names; whenever `run` is present, `run.state` -- not `status` -- is what the source is,
 * and `progress` is the whole run's progress.
 */
export const sourceEventSchema = z.object({
  source_id: z.string(),
  version: z.number().int(),
  stage: z.string(),
  status: z.enum(["running", "done", "failed", "ready", "skipped"]),
  progress: z.number().int().min(0).max(100),
  error: z.string().nullish(),
  run: runSnapshotSchema.nullish(),
});
export type SourceEvent = z.infer<typeof sourceEventSchema>;

export function parseSourceEvent(data: string): SourceEvent | null {
  try {
    const parsed = sourceEventSchema.safeParse(JSON.parse(data));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export interface StageInput {
  status: string;
  stage?: string | null | undefined;
}

/** Console stage for a source row (`status` + backend `stage`) or a live event. */
export function consoleStage({ status, stage }: StageInput): IngestionStage {
  if (status === "failed") return "failed";
  if (status === "ready") return "ready";
  if (status === "queued") return "uploading";
  return (stage ? STAGE_MAP[stage] : undefined) ?? "parsing";
}

/** True while a source is still moving through the pipeline. */
export function isProcessing(status: string): boolean {
  return status === "queued" || status === "processing";
}
